import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

import DeleteIcon
  from "@mui/icons-material/Delete";

import Swal from "sweetalert2";

import {
  createDealGroup,
  deleteDealGroup,
  getAvailableDeals,
  getDealGroups,
} from "../../../utils/dealGroupsAPI";
const DealGroupsSwal = Swal.mixin({
  customClass: {
    container:
      "deal-groups-swal-container",
  },

  didOpen: () => {
    const container =
      Swal.getContainer();

    if (container) {
      container.style.setProperty(
        "z-index",
        "20000",
        "important"
      );
    }
  },
});

const getDealLabel = (deal) => {
  const dealType =
    Number(deal?.dealType || 0);

  if (dealType === 1) {
    return `${deal.quantity}+1`;
  }

  if (dealType === 2) {
    return `${deal.quantity} ב־${deal.dealPrice} ₪`;
  }

  if (dealType === 3) {
    return `${deal.discountPercent}% הנחה`;
  }

  return "ללא מבצע";
};

const areDealsCompatible = (
  first,
  current
) => {
  if (!first || !current) {
    return false;
  }

  return (
    Number(first.dealType) ===
      Number(current.dealType) &&

    Number(first.quantity || 0) ===
      Number(current.quantity || 0) &&

    Number(first.dealPrice || 0) ===
      Number(current.dealPrice || 0) &&

    Number(
      first.discountPercent || 0
    ) ===
      Number(
        current.discountPercent || 0
      )
  );
};

export default function DealGroupsDialog({
  open,
  onClose,
}) {
  const [availableDeals, setAvailableDeals] =
    useState([]);

  const [groups, setGroups] =
    useState([]);

  const [selectedDeals, setSelectedDeals] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const loadData = async () => {
    try {
      setLoading(true);

      const [
        dealsResult,
        groupsResult,
      ] = await Promise.all([
        getAvailableDeals(),
        getDealGroups(),
      ]);

      setAvailableDeals(
        Array.isArray(dealsResult)
          ? dealsResult
          : []
      );

      setGroups(
        Array.isArray(groupsResult)
          ? groupsResult
          : []
      );
    } catch (error) {
      console.error(
        "שגיאה בטעינת קבוצות:",
        error
      );

      await DealGroupsSwal.fire({
        icon: "error",
        title: "שגיאה",
        text:
          error?.response?.data?.message ||
          "לא ניתן לטעון את קבוצות המבצע.",
         
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedDeals([]);
      loadData();
    }
  }, [open]);

  const selectableDeals = useMemo(() => {
    const dealsWithoutGroup =
      availableDeals.filter(
        (deal) =>
          deal.dealGroupId == null
      );

    if (selectedDeals.length === 0) {
      return dealsWithoutGroup;
    }

    const first =
      selectedDeals[0];

    return dealsWithoutGroup.filter(
      (deal) =>
        areDealsCompatible(
          first,
          deal
        )
    );
  }, [
    availableDeals,
    selectedDeals,
  ]);

  const handleSave = async () => {
    if (selectedDeals.length < 2) {
      await DealGroupsSwal.fire({
        icon: "warning",
        title: "לא נבחרו מספיק מוצרים",
        text:
          "יש לבחור לפחות שני מוצרים.",
      });

      return;
    }

    try {
      setSaving(true);

      await createDealGroup(
        selectedDeals.map(
          (deal) => deal.id
        )
      );

      await DealGroupsSwal.fire({
        icon: "success",
        title: "הקבוצה נשמרה",
        text:
          "המוצרים חוברו לאותו מבצע.",
        confirmButtonColor: "#D81B60",
      });

      setSelectedDeals([]);
      await loadData();
    } catch (error) {
      await DealGroupsSwal.fire({
        icon: "error",
        title: "השמירה נכשלה",
        text:
          error?.response?.data?.message ||
          "לא ניתן ליצור את הקבוצה.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (
    dealGroupId
  ) => {
    const result = await DealGroupsSwal.fire({
      icon: "warning",
      title: "לפרק את הקבוצה?",
      text:
        "המבצעים יחזרו לפעול בנפרד.",
      showCancelButton: true,
      confirmButtonText: "כן, לפרק",
      cancelButtonText: "ביטול",
      confirmButtonColor: "#D81B60",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteDealGroup(
        dealGroupId
      );

      await loadData();
    } catch (error) {
      await DealGroupsSwal.fire({
        icon: "error",
        title: "הפירוק נכשל",
        text:
          error?.response?.data?.message ||
          "לא ניתן לפרק את הקבוצה.",
      });
    }
  };

  return (
   <Dialog
  open={open}
  onClose={onClose}
  fullWidth
  maxWidth="md"
  dir="rtl"
  disableEnforceFocus
  disableRestoreFocus
>
      <DialogTitle>
        ניהול קבוצות מבצע
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              לאחר בחירת המוצר הראשון
              יוצגו רק מוצרים עם מבצע זהה.
            </Alert>

            <Autocomplete
              multiple
              options={selectableDeals}
              value={selectedDeals}
              onChange={(
                event,
                newValue
              ) => {
                setSelectedDeals(
                  newValue
                );
              }}
              getOptionLabel={(deal) =>
                `${deal.productName} — ` +
                getDealLabel(deal)
              }
              isOptionEqualToValue={(
                option,
                value
              ) =>
                option.id === value.id
              }
              renderTags={(
                value,
                getTagProps
              ) =>
                value.map(
                  (deal, index) => (
                    <Chip
                      {...getTagProps({
                        index,
                      })}
                      key={deal.id}
                      label={
                        deal.productName
                      }
                    />
                  )
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="בחירת מוצרים"
                  placeholder={
                    selectedDeals.length ===
                    0
                      ? "בחרי מוצר ראשון"
                      : "בחרי מוצרים נוספים"
                  }
                />
              )}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                disabled={
                  saving ||
                  selectedDeals.length < 2
                }
                onClick={handleSave}
                sx={{
                  backgroundColor:
                    "#D81B60",
                }}
              >
                {saving
                  ? "שומר..."
                  : "יצירת קבוצת מבצע"}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography
              variant="h6"
              sx={{ mb: 1 }}
            >
              קבוצות קיימות
            </Typography>

            {groups.length === 0 ? (
              <Typography
                color="text.secondary"
              >
                עדיין לא קיימות קבוצות.
              </Typography>
            ) : (
              <List>
                {groups.map((group) => (
                  <ListItem
                    key={
                      group.dealGroupId
                    }
                    secondaryAction={
                      <IconButton
                        color="error"
                        onClick={() =>
                          handleDelete(
                            group.dealGroupId
                          )
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        `קבוצה ${group.dealGroupId} — ` +
                        getDealLabel(group)
                      }
                      secondary={
                        group.products
                          ?.map(
                            (product) =>
                              product.productName
                          )
                          .join(", ")
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          סגירה
        </Button>
      </DialogActions>
    </Dialog>
  );
}